import { forwardRef } from 'react';

import { Button, Pill } from '@/components';
import { useAnalytics } from '@/lib/analytics';
import { useIsMobile } from '@/lib/hooks';
import { cn, getSessionLabel } from '@/utils';

import { HOBBY_CLASS_IMAGES } from '../../constants';
import { Schedule } from '../../types';

import { getStatusContent, getIcon } from './calendar-item.utils';
import {
  calendarItemVariants,
  calendarPillVariants,
  calendarDateTimeVariants,
  calendarTitleVariants,
} from './calendar-item.variants';

const SESSION_LABEL = getSessionLabel();

export interface CalendarItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  date: string;
  day: string;
  time: string;
  status?: Schedule.Lesson['status'];
  variant?: Schedule.Lesson['variant'];
  start_time?: number;
  joiningLink?: string;
  shouldShowStatus?: boolean;
}

const CalendarItem = forwardRef<HTMLDivElement, CalendarItemProps>(
  (
    {
      className,
      title,
      date,
      day,
      time,
      status,
      variant,
      start_time = 0,
      joiningLink,
      shouldShowStatus,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const { trackEvent } = useAnalytics();

    const handleJoinClick = () => {
      // Track join button click for hobby/master class
      if (variant === 'hobby_class' || variant === 'master_class') {
        trackEvent('clickedSpecialSessionJoin', 1, {
          type: variant,
          sessionTitle: title,
          sessionDate: date,
          sessionTime: time,
        });
      }

      // Open joining link in new tab
      if (joiningLink) {
        window.open(joiningLink, '_blank');
      }
    };

    const getContent = () => {
      if (variant === 'hobby_class' || variant === 'master_class') {
        if (joiningLink && start_time) {
          const currentTimeInSeconds = Date.now() / 1000;
          const twoMinutesBeforeStart = start_time - 2 * 60; // 5 minutes before
          const oneHourAfterStart = start_time + 60 * 60; // 1 hour after

          // Show button if current time is between 5 min before and 1 hour after start_time
          if (
            currentTimeInSeconds >= twoMinutesBeforeStart &&
            currentTimeInSeconds <= oneHourAfterStart
          ) {
            return (
              <Button variant="default" onClick={handleJoinClick}>
                Join {SESSION_LABEL.capitalize}
              </Button>
            );
          }
        }
        return <div className="w-10" />;
      }
      if (status && shouldShowStatus) {
        return (
          <Pill
            variant="solid"
            className={calendarPillVariants({ status, variant })}
            size={isMobile ? 'sm' : 'lg'}
            rounded
          >
            {getStatusContent(status)}
          </Pill>
        );
      }
      return <div className="w-10" />;
    };

    return (
      <div
        className={cn(
          calendarItemVariants({ variant, className }),
          'transition-all duration-300',
          status === 'holiday' &&
            'bg-magical-moonlight-s border border-magical-moonlight-l'
        )}
        style={
          variant === 'hobby_class' && HOBBY_CLASS_IMAGES[title]?.colorValue
            ? {
                background: `radial-gradient(circle ${isMobile ? '70px' : '80px'} at ${isMobile ? '4%' : '2%'} ${isMobile ? '25%' : '45%'}, ${HOBBY_CLASS_IMAGES[title].colorValue} 0%, hsl(31.765 15% 22%) 100%)`,
              }
            : undefined
        }
        ref={ref}
        {...props}
      >
        <div className="flex flex-wrap gap-4 items-center justify-between w-full text-xs md:text-base">
          <div className="flex-3 flex gap-6 items-center justify-start">
            {getIcon(variant, status, title, isMobile)}
            <div className={calendarTitleVariants({ variant })}>{title}</div>
          </div>
          <div className="flex-2 flex items-center justify-end">
            {getContent()}
          </div>
          <div className="flex-2 lg:flex-3 flex gap-3 items-center md:flex-initial md:w-auto w-full md:justify-end justify-center min-[400px]:min-w-80">
            <div className={calendarDateTimeVariants({ variant })}>
              <span className="font-semibold leading-6">{date}</span>
              <span className="text-magical-moonlight-m">|</span>
              <span className="font-semibold leading-6">{day}</span>
              <span className="text-magical-moonlight-m">|</span>
              <span className="font-semibold leading-6">{time}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CalendarItem.displayName = 'CalendarItem';

export { CalendarItem };
export {
  calendarItemVariants,
  calendarPillVariants,
  calendarDateTimeVariants,
  calendarTitleVariants,
} from './calendar-item.variants';
